"use client";

import {
  Heart,
  MessageCircle,
  MoreHorizontal,
  Repeat2,
  Send,
} from "lucide-react";
import { useAnimate, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";

type FeedActionRowProps = {
  likeCount: number;
  commentCount: number;
  quoteCount?: number;
  shareCount: number;
  isLiked: boolean;
  isQuoteActive?: boolean;
  onLike: () => void;
  onOpenLikes?: () => void;
  onComment: () => void;
  onQuote?: () => void;
  onShare: () => void;
  onMore: () => void;
  disabled?: boolean;
  likeDisabled?: boolean;
  commentDisabled?: boolean;
  quoteDisabled?: boolean;
  shareDisabled?: boolean;
  moreDisabled?: boolean;
  likeLabel?: string;
  commentLabel?: string;
  quoteLabel?: string;
  shareLabel?: string;
  moreLabel?: string;
  /**
   * `grid`: five equal centred cells (full-width cards). `x`: spread edge to
   * edge from the content column's left, so the first icon lines up with the
   * text above it (X's timeline).
   */
  layout?: "grid" | "x";
  className?: string;
};

const formatCount = (value: number | undefined) => {
  if (!value || value <= 0) return "";
  if (value > 999_999) return `${Math.floor(value / 1_000_000)}M`;
  if (value > 999) return `${Math.floor(value / 1_000)}K`;
  return value.toLocaleString();
};

/**
 * Optical sizes. Lucide draws every glyph in the same 24px box but they fill
 * it very differently — the bubble and the paper plane fill it, the repost
 * arrows are short and flat, the dots are tiny — so one pixel size reads as
 * five. These make them LOOK the same size on the row.
 */
const ICON_SIZE: Record<string, string> = {
  like: "size-[19px]",
  comment: "size-[18px]",
  quote: "size-[21px]",
  share: "size-[17px]",
  more: "size-[20px]",
};

/** X's muted grey: a step under the tertiary text colour. */
const ROW_TONE = "text-text-tertiary/80";

export function FeedActionRow({
  likeCount,
  commentCount,
  quoteCount = 0,
  shareCount,
  isLiked,
  isQuoteActive,
  onLike,
  onOpenLikes,
  onComment,
  onQuote,
  onShare,
  onMore,
  disabled = false,
  likeDisabled = false,
  commentDisabled = false,
  quoteDisabled = false,
  shareDisabled = false,
  moreDisabled = false,
  likeLabel = "Like",
  commentLabel = "Comment",
  quoteLabel = "Quote repost",
  shareLabel = "Share",
  moreLabel = "More",
  layout = "grid",
  className,
}: FeedActionRowProps) {
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressActiveRef = useRef(false);
  // Bumped whenever the user likes (not unlikes) so the heart re-runs its
  // pop animation.
  const [likePopKey, setLikePopKey] = useState(0);
  const [likeScope, animatePop] = useAnimate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (likePopKey > 0 && !reduceMotion && likeScope.current) {
      animatePop(
        likeScope.current,
        { scale: [null, 1.32, 0.92, 1] },
        { duration: 0.32, ease: "easeOut" },
      );
    }
  }, [likePopKey, reduceMotion, animatePop, likeScope]);

  const handleLikePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return; // Only primary button clicks/touches
    isLongPressActiveRef.current = false;

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    longPressTimerRef.current = setTimeout(() => {
      isLongPressActiveRef.current = true;
      if (onOpenLikes) {
        uiHaptics.longPress();
        onOpenLikes();
      }
    }, 500); // 500ms threshold
  };

  const handleLikePointerUp = (_e: React.PointerEvent) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleLikePointerLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleLikeClick = (e: React.MouseEvent) => {
    if (isLongPressActiveRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (!isLiked) {
      setLikePopKey((key) => key + 1);
    }
    uiHaptics.like();
    onLike();
  };

  const actions = [
    {
      key: "like",
      label: likeLabel,
      count: likeCount,
      icon: Heart,
      active: isLiked,
      activeClass: "text-error",
      fillClass: "fill-error text-error",
      onClick: onLike,
      disabled: disabled || likeDisabled,
    },
    {
      key: "comment",
      label: commentLabel,
      count: commentCount,
      icon: MessageCircle,
      onClick: onComment,
      disabled: disabled || commentDisabled,
    },
    {
      key: "quote",
      label: quoteLabel,
      count: quoteCount,
      icon: Repeat2,
      active: isQuoteActive,
      activeClass: "text-success",
      fillClass: "text-success",
      onClick: onQuote ?? (() => {}),
      disabled: disabled || quoteDisabled || !onQuote,
    },
    {
      key: "share",
      label: shareLabel,
      count: shareCount,
      icon: Send,
      onClick: onShare,
      disabled: disabled || shareDisabled,
    },
    {
      key: "more",
      label: moreLabel,
      count: undefined,
      icon: MoreHorizontal,
      onClick: onMore,
      disabled: disabled || moreDisabled,
    },
  ];

  return (
    <div
      data-feed-action-row="true"
      className={cn(
        "pt-0.5 pb-1.5",
        layout === "x"
          ? "-mx-1 flex items-center justify-between"
          : "grid grid-cols-5 px-0.5",
        className,
      )}
    >
      {actions.map((action) => {
        const Icon = action.icon;
        const countLabel = formatCount(action.count);

        if (action.key === "like") {
          return (
            <div
              key={action.key}
              className={cn(
                "col-span-1 flex items-center gap-0.5",
                layout === "x" ? "justify-start" : "justify-center",
              )}
            >
              <button
                type="button"
                onClick={handleLikeClick}
                onPointerDown={handleLikePointerDown}
                onPointerUp={handleLikePointerUp}
                onPointerCancel={handleLikePointerLeave}
                onPointerLeave={handleLikePointerLeave}
                disabled={action.disabled}
                title={action.label}
                aria-label={action.label}
                aria-pressed={action.active}
                className={cn(
                  "flex h-10 items-center justify-center transition hover:text-text-primary active:scale-90",
                  ROW_TONE,
                  countLabel ? "rounded-l-md px-1.5" : "w-full rounded-md px-1",
                  countLabel && "hover:bg-surface-alt",
                  !countLabel && "hover:bg-surface-alt",
                  action.active && action.activeClass,
                  action.disabled && "cursor-not-allowed opacity-45",
                )}
              >
                <span ref={likeScope} className="inline-flex">
                  <Icon
                    strokeWidth={1.75}
                    className={cn(
                      "shrink-0",
                      ICON_SIZE[action.key],
                      action.active && action.fillClass,
                    )}
                    aria-hidden="true"
                  />
                </span>
              </button>
              {countLabel ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenLikes) onOpenLikes();
                  }}
                  disabled={action.disabled}
                  className={cn(
                    "flex h-10 items-center justify-center rounded-r-md pr-1.5 pl-0.5 text-[13px] leading-none tabular-nums transition hover:bg-surface-alt hover:text-text-primary active:scale-95",
                    ROW_TONE,
                    action.disabled && "cursor-not-allowed opacity-45",
                  )}
                >
                  <span
                    key={`${action.count}`}
                    className="animate-like-count-in"
                  >
                    {countLabel}
                  </span>
                </button>
              ) : null}
            </div>
          );
        }

        return (
          <button
            key={action.key}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            title={action.label}
            aria-label={action.label}
            aria-pressed={action.key === "like" ? action.active : undefined}
            className={cn(
              "flex h-10 min-w-0 items-center justify-center gap-1 rounded-md px-1 transition hover:bg-surface-alt hover:text-text-primary active:scale-95",
              ROW_TONE,
              action.active && action.activeClass,
              action.disabled && "cursor-not-allowed opacity-45",
            )}
          >
            <Icon
              strokeWidth={1.75}
              className={cn(
                "shrink-0",
                ICON_SIZE[action.key],
                action.active && action.fillClass,
              )}
              aria-hidden="true"
            />
            {countLabel ? (
              <span className="min-w-0 truncate text-[13px] leading-none tabular-nums">
                {countLabel}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
